import { Building2, Factory, MapPin, Star, Store } from 'lucide-react';
import { clsx } from 'clsx';
import { hasCapability, type BuildingCapability, type BuildingIconName, type BuildingKind } from '@tradeoutlet/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { Select } from '@/components/ui';

/** Fallback labels for the built-in type keys (the real list is admin-editable: useBuildingTypes). */
export const BUILDING_KINDS: { id: BuildingKind; label: string }[] = [
  { id: 'warehouse', label: 'Warehouse' }, { id: 'store', label: 'Store' }, { id: 'office', label: 'Office' }, { id: 'site', label: 'Site' }, { id: 'other', label: 'Other' },
];

/** `icon` (from the building's type) wins; built-in keys double as icons for older data. */
export function BuildingIcon({ kind, icon, className = 'h-4 w-4' }: { kind?: BuildingKind | null; icon?: BuildingIconName | null; className?: string }) {
  const k = icon ?? kind;
  if (k === 'warehouse') return <Factory className={className} />;
  if (k === 'store') return <Store className={className} />;
  if (k === 'office') return <Building2 className={className} />;
  return <MapPin className={className} />;
}

export function useBuildingTypes() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['building-types', ws], queryFn: () => api.buildingTypes(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Display name of a type key, from the editable list (falls back to the built-in label or the key). */
export function useBuildingTypeName() {
  const t = useBuildingTypes();
  return (key?: string | null) => (key ? t.data?.types.find(x => x.key === key)?.name ?? BUILDING_KINDS.find(k => k.id === key)?.label ?? key : '');
}

/** Keep buildings in `allowed` (plus `keep`, the current value); if none qualify, show all rather than an empty picker. */
export function onlyCapable<T extends { id: string }>(list: T[], allowed: Set<string> | undefined, keep?: string | null): T[] {
  if (!allowed?.size) return list;
  return list.filter(l => allowed.has(l.id) || l.id === keep);
}

/** Buildings whose type has a capability (feedback #6). Undefined while loading. */
export function useBuildingsWith(cap: BuildingCapability) {
  const b = useBuildings();
  return b.data ? new Set(b.data.buildings.filter(x => hasCapability(x, cap)).map(x => x.id)) : undefined;
}

export function useBuildings() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['buildings', ws], queryFn: () => api.buildings(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Building select used on tasks, projects, chats and filters. */
export function BuildingSelect({ value, onChange, disabled, placeholder = 'No building', className }: { value: string | null | undefined; onChange: (id: string | null) => void; disabled?: boolean; placeholder?: string; className?: string }) {
  const b = useBuildings();
  return (
    <Select disabled={disabled} className={className} value={value ?? ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">{placeholder}</option>
      {(b.data?.buildings ?? []).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
    </Select>
  );
}

export function BuildingTag({ id }: { id: string | null | undefined }) {
  const b = useBuildings();
  const x = id ? b.data?.buildings.find(y => y.id === id) : undefined;
  if (!x) return null;
  return (
    <Link to={`/buildings/${x.id}`} title={`Building: ${x.name}`}
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded text-xs text-slate-500 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200" data-testid="channel-building">
      <BuildingIcon kind={x.kind} icon={x.icon} className="h-3 w-3" />{x.name}
    </Link>
  );
}

/** t124: compact building icon + name (and area) on task cards and rows; plain text so the row's own click still opens the task. */
export function BuildingLabel({ id, area, className }: { id: string | null | undefined; area?: string | null; className?: string }) {
  const b = useBuildings();
  const x = id ? b.data?.buildings.find(y => y.id === id) : undefined;
  if (!x) return null;
  return (
    <span className={clsx('inline-flex min-w-0 items-center gap-1 text-xs text-slate-600 dark:text-slate-300', className)} title={`Building: ${x.name}${area ? ` · ${area}` : ''}`} data-testid="task-building">
      <BuildingIcon kind={x.kind} icon={x.icon} className="h-3 w-3 shrink-0" /><span className="truncate">{x.name}{area ? ` · ${area}` : ''}</span>
    </span>
  );
}

/**
 * Follow / Following as one toggle (review Low: the two states looked like different buttons).
 * Same outline shape in both states; pressed = filled star + tinted fill, announced with aria-pressed.
 */
export function FollowToggle({ following, onToggle, name, size = 'sm' }: { following: boolean; onToggle: () => void; name: string; size?: 'sm' | 'md' }) {
  return (
    <button type="button" aria-pressed={following} onClick={onToggle} data-testid="follow-toggle"
      title={following ? `Stop following ${name}` : `Follow ${name}`}
      className={clsx('inline-flex shrink-0 items-center gap-1.5 rounded-lg font-medium ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:focus-visible:ring-slate-100',
        size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3 text-sm',
        following
          ? 'bg-slate-100 text-slate-900 ring-slate-300 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-600 dark:hover:bg-slate-700'
          : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800')}>
      <Star aria-hidden className={clsx(size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4', following ? 'fill-slate-900 text-slate-900 dark:fill-slate-100 dark:text-slate-100' : 'text-slate-500')} />
      {following ? 'Following' : 'Follow'}
    </button>
  );
}
